import { Dispatch, SetStateAction, useEffect } from 'react';
import { signOut } from 'next-auth/react';
import Image from 'next/image';
import useModal from '@/hooks/useModal';
import { Button, Typography } from '@/components/common';
import { X } from 'lucide-react';
import { useNotification } from '@/hooks';
import { userService } from '@/api';
import AppRoutes from '@/const/routes';
import { formatErrorMessage } from '@/lib';

interface Props {
	isOpen: boolean;
	setModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const ModalDelete: React.FC<Props> = ({ isOpen, setModalOpen }) => {
	const { Modal, show, hide } = useModal();
	const { toastSuccess, toastError } = useNotification();

	useEffect(() => {
		if (isOpen) {
			show();
		} else {
			hide();
		}
	}, [isOpen]);

	const deleteAccount = async (): Promise<void> => {
		try {
			await userService.delete();
			toastSuccess(
				<span>
					Your account has been successfully deleted. <br /> <br />
					{`We're sorry to see you
					go. If you change your mind, you're always welcome to reactivate your
					account!`}
				</span>
			);

			setTimeout(() => {
				signOut();
				window.location.replace(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
			}, 1000);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	return (
		<Modal classNameCardContainer={'md:!w-[532px] md:!h-[384px] lg:!px-10'}>
			<div className="flex flex-col items-center w-full   gap-6 relative">
				<div className="flex items-center gap-2 ">
					<div className="bg-purple-150 p-2 rounded-xl">
						<Image
							src={'/img/common/hat.png'}
							alt=""
							width={23}
							height={23}
							quality={75}
						/>
					</div>
					<Typography type="semibold-20">Delete account?</Typography>
					<button
						className="absolute right-6 top-2"
						onClick={() => setModalOpen(false)}
					>
						<X />
					</button>
				</div>
				<div className="w-full">
					<Typography type="semibold-16" className="text-cente">
						Are you sure you want to delete your account?
					</Typography>
					<Typography type="regular-16" className="text-center">
						If you proceed, you will lose all your data and access to your
						information.
					</Typography>
					<br />
					<Typography type="semibold-16" className="text-cente">
						{`We’d love for you to stay!`}
					</Typography>
					<Typography type="regular-16" className="text-center">
						Your account is secure, and you can return anytime. If you have
						concerns, please reach out!
					</Typography>
				</div>
				<div className="flex items-center gap-2">
					<Button
						decoration="secondary"
						size="medium"
						onClick={() => setModalOpen(false)}
					>
						Cancel
					</Button>
					<Button
						decoration="danger-outline"
						size="medium"
						onClick={deleteAccount}
						className="bg-white"
					>
						Delete
					</Button>
				</div>
			</div>
		</Modal>
	);
};
